"use client";
import GenericAutoComplete from "@/components/GenericAutoComplete";
import GenericSelectionModal from "@/components/GenericSelectionModal";
import {
  createDynamicFerryPassengerConfig,
  ferryPassengerDisplayFormatter,
} from "@/configs/ferry/search";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FerryChildSelect from "./FerryChildSelect";
import FerryDatePicker from "./FerryDatePicker";
import FerrySearchTabs from "./FerrySearchTabs";
import FerryVehicleSelect from "./FerryVehicleSelect";

import GenericDropdown from "@/components/GenericDropdown";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useFerrySearch } from "@/hooks/useFerrySearch";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { dateFormats, isBefore } from "@/utils/dateFormatter";

const Form = (props) => {
  const {
    initialOrigin,
    initialDestination,
    initialDepartureDate,
    initialReturnDate,
    initialPassengers,
    initialActiveTab,
    initialIsCouponSale,
    scrollToForm,
    searchButtonTextClassName = "inline md:hidden xl:inline",
    searchButtonTurnaIconClassName = "!inline-block md:!hidden xl:!block",
    searchButtonMagnifierIconClassName = "!hidden md:!inline-block xl:!hidden",
    viewportHeightWrapperActive = false,
  } = props;
  const locale = useLocale();

  const t = useTranslationDetector();
  const tForm = useTranslationDetector();
  const router = useRouter();
  // Ferry-specific storage key
  const STORAGE_KEY = "latestFerrySearches";
  // Initial data objesini oluştur - useMemo ile optimize et
  const initialData = useMemo(() => {
    const data = { storageKey: STORAGE_KEY };
    if (initialOrigin) data.departureCity = initialOrigin;
    if (initialDestination) data.returnCity = initialDestination;
    if (initialDepartureDate) data.departureDate = initialDepartureDate;
    if (initialReturnDate) data.returnDate = initialReturnDate;
    if (initialPassengers) data.passengers = initialPassengers;
    if (initialActiveTab) data.activeTab = initialActiveTab;
    if (initialIsCouponSale) data.isCouponSale = initialIsCouponSale;
    return data;
  }, [
    initialOrigin,
    initialDestination,
    initialDepartureDate,
    initialReturnDate,
    initialPassengers,
    initialActiveTab,
    initialIsCouponSale,
  ]);

  // Use ferry search hook
  const {
    fromCity,
    toCity,
    departureDate,
    returnDate,
    passengers,
    latestFerrySearches,
    popularDestinations,
    ensurePopularLoaded,
    setDepartureDate,
    setReturnDate,
    toggleFromAndTo,
    searchForFerryOrigins,
    searchForFerryDestinations,
    onSelectFromCity,
    onSelectToCity,
    validateForm,
    generateFerrySearchUrl,
    publicHolidays,
    refreshPublicHolidays,
    activeTab,
    setActiveTab,
    isWithVehicle,
    setIsWithVehicle,
    vehicleType,
    setVehicleType,
    childAges,
    setChildAges,
    setPassengers,
    isCouponSale,
    ticketCount,
    setTicketCount,
    ferryVehicles,
    handleSearchCouponSale,
    setTempPassengers,
    tempPassengers,
  } = useFerrySearch(initialData, t);

  const [validationErrors, setValidationErrors] = useState({});
  const passengerModalRef = useRef(null);
  const ignorePassengerChangeRef = useRef(false);
  const { warningModal } = useModalHelpers();

  // Config'i optimize et - sadece adult veya child değeri değiştiğinde yeniden hesapla
  const passengerConfig = useMemo(() => {
    const adultCount = tempPassengers?.adult || 0;
    const childCount = tempPassengers?.child || 0;

    return createDynamicFerryPassengerConfig({
      tempPassengers: {
        adult: adultCount,
        child: childCount,
      },
      isWithVehicle: tempPassengers?.isWithVehicle ?? isWithVehicle,
      maxPassengers: 9,
      minPassengers: 1,
    });
  }, [
    tempPassengers?.adult,
    tempPassengers?.child,
    tempPassengers?.isWithVehicle,
    isWithVehicle,
  ]);

  const ticketCountOptions = ["1", "2", "3", "4", "5", "6"];

  const handleFromCitySelect = useCallback(
    (city) => {
      onSelectFromCity(city);
      setValidationErrors((prev) => ({ ...prev, departureCity: null }));
    },
    [onSelectFromCity],
  );

  const clearFromCitySelection = useCallback(() => {
    onSelectFromCity(null);
  }, [onSelectFromCity]);

  const handleToCitySelect = useCallback(
    (city) => {
      onSelectToCity(city);
      setValidationErrors((prev) => ({ ...prev, returnCity: null }));
    },
    [onSelectToCity],
  );

  const clearToCitySelection = useCallback(() => {
    onSelectToCity(null);
  }, [onSelectToCity]);

  // Handle search history item selection
  const handleSearchHistorySelect = useCallback(
    (search, onClosePortal) => {
      // Fill all form fields with the selected search data

      setActiveTab(search.isOneWay ? "oneway" : "roundtrip");
      if (search.departureCity) {
        onSelectFromCity(search.departureCity);
      }
      if (search.returnCity) {
        onSelectToCity(search.returnCity);
      }
      if (search.departureDateRaw) {
        // Parse the date from localStorage
        const parsedDate = new Date(search.departureDateRaw);
        if (isBefore(parsedDate, new Date())) {
          setDepartureDate(
            new Date(new Date().setDate(new Date().getDate() + 1)),
          );
        } else {
          setDepartureDate(parsedDate);
        }
      }
      if (search.returnDateRaw) {
        // Parse the date from localStorage
        const parsedDate = new Date(search.returnDateRaw);
        if (isBefore(parsedDate, new Date())) {
          setReturnDate(new Date(new Date().setDate(new Date().getDate() + 2)));
        } else {
          setReturnDate(parsedDate);
        }
      } else {
        setReturnDate(null);
      }
      if (search.passengers) {
        setPassengers(search.passengers);
        ignorePassengerChangeRef.current = true;
        setTempPassengers(search.passengers);
      }
      // Clear any validation errors
      setValidationErrors({});

      // Mobile portal'ı kapat
      if (onClosePortal) {
        onClosePortal();
      }
    },
    [
      onSelectFromCity,
      onSelectToCity,
      setDepartureDate,
      setReturnDate,
      setActiveTab,
      setPassengers,
      setTempPassengers,
    ],
  );

  // Render function for default content (when not searching)
  const renderDefaultContent = useCallback(
    ({ onSelectItem, onClosePortal }) => {
      return (
        <div className="flex flex-col gap-5 text-sm">
          {latestFerrySearches && (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {t("ferry.recentSearches")}
              </span>
              <div className="flex w-full flex-col items-start justify-between gap-4">
                {latestFerrySearches.map((search, index) => (
                  <button
                    className="flex w-full cursor-pointer items-start justify-between gap-6"
                    onClick={() =>
                      handleSearchHistorySelect(search, onClosePortal)
                    }
                    key={index}
                  >
                    <div className="flex items-center gap-4">
                      <FontAwesomeIcon
                        className="!h-4 !w-4"
                        icon={byPrefixAndName.far["clock-rotate-left"]}
                      />
                      <div className="flex flex-col gap-1 text-left">
                        <div className="flex items-center justify-start gap-[6px] text-sm/19 font-semibold">
                          <span>
                            {search.departureCity.label.split("-")[0].trim()}
                          </span>
                          {search.isOneWay ? (
                            <FontAwesomeIcon
                              className="!h-[14px] !w-[14px]"
                              icon={byPrefixAndName.fak["half-right-arrow"]}
                            />
                          ) : (
                            <FontAwesomeIcon
                              className="!h-[14px] !w-[14px]"
                              icon={
                                byPrefixAndName.fak["half-round-trip-arrows"]
                              }
                            />
                          )}

                          <span>
                            {search.returnCity.label.split("-")[0].trim()}
                          </span>
                        </div>
                        <div className="text-foreground-muted text-sm/19">
                          {search.isOneWay
                            ? dateFormats.dayShortMonth(
                                search.departureDateRaw,
                                locale,
                              )
                            : `${dateFormats.dayShortMonth(search.departureDateRaw, locale)} - ${dateFormats.dayShortMonth(search.returnDateRaw, locale)}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Popular destinations */}
          {!popularDestinations || popularDestinations.length === 0 ? (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("ferry.popularDestinations")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {/* Placeholder chips */}
                <span className="bg-background-400 h-8 w-24 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-20 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-28 animate-pulse rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("ferry.popularDestinations")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {popularDestinations.map((item, index) => (
                  <button
                    key={index}
                    className="border-border cursor-pointer rounded-lg border px-3 py-[7.5px] text-base/21 font-normal"
                    onClick={() => onSelectItem(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      latestFerrySearches,
      popularDestinations,
      tForm,
      handleSearchHistorySelect,
      locale,
    ],
  );

  // Render function for no results
  const renderNoResults = useCallback(
    ({ isLoading, inputValue, clearInput }) => {
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
          <div className="flex flex-col gap-2">
            <span className="text-center text-lg font-semibold">
              {tForm("ferry.noResultsFound")}
            </span>
            <span className="text-center text-sm">
              {tForm("ferry.changeSearchCriteria")}
            </span>
          </div>
        </div>
      );
    },
    [tForm],
  );
  // Render function for search results
  const renderSearchResults = useCallback(({ searchResults, onSelectItem }) => {
    return (
      <div className="flex flex-col gap-4 text-sm">
        {searchResults?.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
            <Image
              src={`${cdnCommonPath}telescope.svg`}
              alt="No results"
              width={60}
              height={60}
            />
            <span className="text-center text-lg font-semibold">
              {tForm("ferry.noResultsFound")}
            </span>
            <span className="text-center text-sm">
              {tForm("ferry.changeSearchCriteria")}
            </span>
          </div>
        )}
        {searchResults?.length > 0 &&
          searchResults?.map((searchResult) => (
            <button
              className="flex cursor-pointer items-start justify-between gap-6"
              key={searchResult.id}
              onClick={() => onSelectItem(searchResult)}
            >
              <div className="flex items-center gap-4 text-left">
                <FontAwesomeIcon icon={byPrefixAndName.fas["ferry"]} />
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-[6px] font-semibold">
                    {searchResult.label}
                  </div>
                  <div className="text-foreground-muted">
                    {searchResult.subLabel}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">{searchResult.code}</div>
            </button>
          ))}
      </div>
    );
  }, []);

  const handlePassengerChange = useCallback(
    (values) => {
      if (ignorePassengerChangeRef.current) {
        ignorePassengerChangeRef.current = false;
        return;
      }

      // Mevcut ve yeni değerleri karşılaştır
      const currentAdult = tempPassengers?.adult || 0;
      const currentChild = tempPassengers?.child || 0;
      const newAdult = values?.adult || 0;
      const newChild = values?.child || 0;

      // Child ages'i trim et
      const count = Number(values?.child) || 0;
      let trimmedChildAges = [];
      if (Array.isArray(values?.childAges)) {
        trimmedChildAges = values.childAges.slice(0, count);
      }

      // Child ages karşılaştırması için string comparison
      const currentChildAgesStr = JSON.stringify(
        tempPassengers?.childAges || [],
      );
      const newChildAgesStr = JSON.stringify(trimmedChildAges);

      // Değişiklik varsa update et
      if (
        currentAdult !== newAdult ||
        currentChild !== newChild ||
        currentChildAgesStr !== newChildAgesStr ||
        tempPassengers?.isWithVehicle !== values?.isWithVehicle ||
        tempPassengers?.vehicleType !== values?.vehicleType
      ) {
        const nextPassengers = {
          adult: newAdult,
          child: newChild,
          childAges: trimmedChildAges,
          isWithVehicle: values?.isWithVehicle || false,
          vehicleType: values?.vehicleType || null,
        };

        // Hem geçici state'i hem de asıl passengers state'ini güncelle
        setTempPassengers(nextPassengers);
        setPassengers(nextPassengers);
      }
    },
    [tempPassengers, setTempPassengers, setPassengers],
  );
  const passengerCloseRef = useRef(false);
  const handlePassengerModalClose = useCallback(() => {
    if (passengerCloseRef.current) return;
    passengerCloseRef.current = true;

    const next = passengers || null;
    const current = tempPassengers || null;
    const isSame = JSON.stringify(current) === JSON.stringify(next);

    if (!isSame) {
      ignorePassengerChangeRef.current = true;
      setTempPassengers(next);
    }

    requestAnimationFrame(() => {
      passengerCloseRef.current = false;
    });
  }, [passengers, tempPassengers, setTempPassengers]);

  const handlePassengerSelect = useCallback(
    (values) => {
      // Persist child ages trimmed to count
      const count = Number(values?.child) || 0;
      let trimmedChildAges = [];
      if (Array.isArray(values?.childAges)) {
        trimmedChildAges = values.childAges.slice(0, count);
      }

      // Passenger data'sını tek seferde kaydet
      setPassengers({
        adult: values?.adult || 1,
        child: values?.child || 0,
        childAges: trimmedChildAges,
        isWithVehicle: values?.isWithVehicle || false,
        vehicleType: values?.vehicleType || null,
      });

      // tempPassengers'ı temizle
      setTempPassengers(null);
    },
    [setPassengers, setTempPassengers],
  );
  const toggleCities = useCallback(() => {
    toggleFromAndTo();
    // Toggle sonrasında validation hatalarını temizle
    // Çünkü artık yeni şehir yerleşimi var ve hata durumu değişmiş olabilir
    setValidationErrors({});
  }, [toggleFromAndTo]);

  const validateAndSearch = useCallback(() => {
    const errors = validateForm();
    if (activeTab === "roundtrip" && !returnDate) {
      errors.returnDate = "Dönüş tarihi seçiniz";
    }
    if (isCouponSale) {
      if (!vehicleType) {
        errors.vehicleType = "Araç tipi seçiniz";
      }
    }
    // Çocuk yaşı seçimi kontrolü (otel formundaki mantığa benzer)
    const childCount = passengers?.child || 0;
    const childAgesArray = Array.isArray(passengers?.childAges)
      ? passengers.childAges
      : [];
    const filledChildAgesCount = childAgesArray.filter(
      (age) => age !== "" && age != null,
    ).length;

    if (childCount > 0 && filledChildAgesCount !== childCount) {
      errors.childrenAges = tForm("ferry.childrenAgesRequiredMessage");
    }
    if (isWithVehicle && !vehicleType) {
      errors.vehicleType = tForm("ferry.vehicleTypeRequired");
    }
    if (Object.keys(errors).length === 0) {
      // Validasyon başarılı - hatalar varsa temizle
      setValidationErrors({});
      if (isCouponSale) {
        handleSearchCouponSale();
        return;
      }
      // Son arama bilgilerini localStorage'a kaydet
      const newSearch = {
        departureCity: fromCity,
        returnCity: toCity,
        isOneWay: activeTab === "oneway",
        departureDateRaw: departureDate,
        departureDate: departureDate?.toLocaleDateString("tr-TR"),
        returnDate:
          activeTab === "roundtrip"
            ? returnDate?.toLocaleDateString("tr-TR")
            : null,
        returnDateRaw: activeTab === "roundtrip" ? returnDate : null,
        passengers,
      };

      // Mevcut arama geçmişini al
      const existingSearches = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "[]",
      );
      // Yeni aramayı array'in başına ekle
      const updatedSearches = [newSearch, ...existingSearches];

      // localStorage'a kaydet
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSearches));

      // URL oluştur ve yönlendir
      const url = generateFerrySearchUrl();
      if (url) {
        // router.push(url);
        window.location.href = url; // no ajx
      }
    } else {
      // Validasyon başarısız - hataları göster
      setValidationErrors(errors);

      // Sadece çocuk yaşı hatası varsa bottom sheet uyarısı göster
      if (errors.childrenAges && Object.keys(errors).length === 1) {
        warningModal({
          title: tForm("ferry.childrenAgesRequiredTitle"),
          message: tForm("ferry.childrenAgesRequiredMessage"),
          isBottomSheet: true,
          onClose: () => {
            passengerModalRef.current?.open();
          },
        });
      }
      if (errors.vehicleType && Object.keys(errors).length === 1) {
        warningModal({
          title: tForm("ferry.vehicleTypeRequiredTitle"),
          message: tForm("ferry.vehicleTypeRequiredMessage"),
          isBottomSheet: true,
          onClose: () => {
            passengerModalRef.current?.open();
          },
        });
      }
    }
  }, [
    validateForm,
    fromCity,
    toCity,
    departureDate,
    returnDate,
    activeTab,
    generateFerrySearchUrl,
    router,
    passengers,
    isCouponSale,
    warningModal,
    tForm,
  ]);

  useEffect(() => {
    if (activeTab !== "roundtrip" || returnDate) {
      setValidationErrors((prev) => {
        if (!prev?.returnDate) return prev;
        const { returnDate: _returnDate, ...rest } = prev;
        return rest;
      });
    }
  }, [activeTab, returnDate]);

  return (
    <div className="flex flex-col gap-4 md:gap-4">
      <FerrySearchTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="shadow-medium flex flex-col gap-0 rounded-xl bg-white md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
        <div className="flex w-full flex-col md:flex-row md:gap-4">
          <div className="md:shadow-medium flex flex-col items-center rounded-lg bg-white shadow-none md:w-4/4 md:flex-row">
            <div className="relative z-[1] flex w-full flex-col items-end md:static md:w-[57%] md:flex-row md:items-center">
              {/* AutoComplete - Nereden */}
              <div className="w-full md:w-1/2">
                <GenericAutoComplete
                  id="ferry-from"
                  label={tForm("ferry.from")}
                  searchFunction={searchForFerryOrigins}
                  onSelect={handleFromCitySelect}
                  onClearSelection={clearFromCitySelection}
                  value={fromCity?.value || ""}
                  error={validationErrors.departureCity}
                  translationNamespace="ferry"
                  renderDefaultContent={renderDefaultContent}
                  onOpen={ensurePopularLoaded}
                  renderSearchResults={renderSearchResults}
                  renderNoResults={renderNoResults}
                  scrollToForm={scrollToForm}
                  showCloseIconMobile
                  inputClassName="font-semibold!"
                  webDropDownClassName={clsx("rtl:right-0!")}
                  debounce={300}
                  minQueryLength={0}
                />
              </div>

              <span className="relative z-[11] h-[1px] w-[1px] overflow-visible md:h-6 md:w-6">
                <button
                  onClick={toggleCities}
                  className="border-border-weaker flex h-11 w-11 -translate-x-[52px] -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border-2 bg-white md:-translate-x-[10px] md:-translate-y-[10px]"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["arrow-up-arrow-down"]}
                    className="md:rotate-90"
                  />
                </button>
              </span>

              {/* AutoComplete - Nereye */}
              <div className="w-full md:w-1/2">
                <GenericAutoComplete
                  showCloseIconMobile
                  id="ferry-to"
                  label={tForm("ferry.to")}
                  searchFunction={
                    fromCity?.id
                      ? searchForFerryDestinations
                      : searchForFerryOrigins
                  }
                  onSelect={handleToCitySelect}
                  onClearSelection={clearToCitySelection}
                  value={toCity?.value}
                  error={validationErrors.returnCity}
                  translationNamespace="ferry"
                  renderDefaultContent={
                    fromCity?.id ? renderSearchResults : renderDefaultContent
                  }
                  onOpen={ensurePopularLoaded}
                  renderSearchResults={renderSearchResults}
                  renderNoResults={renderNoResults}
                  shouldShearchOnClick={fromCity?.id ? true : false}
                  // showWarningOnSearchClick={fromCity?.id ? false : true}
                  scrollToForm={scrollToForm}
                  inputClassName="font-semibold!"
                  debounce={300}
                  minQueryLength={0}
                />
              </div>
            </div>

            <span className="bg-border mx-1 hidden h-[34px] w-[1px] md:block"></span>

            {/* Date Selection */}
            <div
              className={clsx(
                "flex w-full items-center md:w-[43%] md:border-0",
                isCouponSale ? "hidden" : "",
                activeTab === "roundtrip" ? "" : "",
              )}
            >
              <FerryDatePicker
                activeTab={activeTab}
                departureDate={departureDate}
                returnDate={returnDate}
                setDepartureDate={setDepartureDate}
                setReturnDate={setReturnDate}
                publicHolidays={publicHolidays}
                onOpen={refreshPublicHolidays}
                validationErrors={validationErrors}
                setActiveTab={setActiveTab}
                scrollToForm={scrollToForm}
                viewportHeightWrapperActive={viewportHeightWrapperActive}
              />
            </div>
            {/* Coupon Sale Inputs - mobilde yan yana (grid 103px + 1fr), desktop'ta aynı düzen */}
            <div
              className={clsx(
                "border-primary-weak grid w-full grid-cols-[103px_1fr] items-center gap-0 border-b md:flex md:w-auto md:grid-cols-none md:gap-0 md:border-0",
                isCouponSale ? "" : "!hidden",
              )}
            >
              <div className="border-border-weak flex min-w-0 items-center border-r md:w-auto md:flex-none md:border-r-0">
                <div className="w-full min-w-0 md:w-[103px] lg:w-[130px]">
                  <GenericDropdown
                    id="vehicle-count"
                    label={tForm("ferry.count")}
                    error={validationErrors.ticketCount}
                    value={ticketCount ? String(ticketCount) : "1"}
                    options={ticketCountOptions}
                    onSelect={setTicketCount}
                    triggerClassName="rounded-lg"
                    buttonClassName="px-3 md:px-3"
                    dropdownClassName="w-full"
                    scrollToForm={scrollToForm}
                  />
                </div>
              </div>
              <span className="bg-border mx-1 hidden h-[34px] w-[1px] shrink-0 md:block" />
              <div className="flex min-w-0 items-center md:w-auto md:flex-none">
                <div className="w-full min-w-0 md:w-[328px] lg:w-[402px]">
                  <GenericDropdown
                    id="vehicle-type"
                    label={tForm("ferry.vehicleType")}
                    error={validationErrors.vehicleType}
                    value={vehicleType}
                    options={ferryVehicles}
                    onSelect={setVehicleType}
                    applyWidthOnMobile={false}
                    triggerClassName="rounded-lg"
                    buttonClassName="px-4"
                    dropdownClassName="w-full"
                    scrollToForm={scrollToForm}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Passenger Selection */}
          <div
            className={clsx(
              "w-full md:mt-0 md:w-1/4 md:max-w-[182px]",
              isCouponSale ? "hidden" : "",
            )}
          >
            <GenericSelectionModal
              ref={passengerModalRef}
              config={passengerConfig}
              label={tForm("ferry.passengerTitle")}
              placeholder={tForm("ferry.passengerSelect")}
              value={tempPassengers?.adult ? tempPassengers : passengers}
              onSelect={handlePassengerSelect}
              onChange={handlePassengerChange}
              // footerClassName="max-h-[305px] md:max-h-auto overflow-y-auto"
              footerClassName="max-h-[305px] overflow-y-auto md:max-h-none md:overflow-visible"
              displayFormatter={(values) =>
                ferryPassengerDisplayFormatter(values, tForm)
              }
              applyButtonText={tForm("ferry.passengerApply")}
              onDropdownClose={handlePassengerModalClose}
              renderHeader={(props) => {
                if (ferryVehicles && ferryVehicles.length > 0) {
                  return (
                    <FerryVehicleSelect
                      tempValues={props.tempValues}
                      setTempValues={props.setTempValues}
                      ferryVehicles={ferryVehicles}
                      defaultVehicleType={vehicleType}
                    />
                  );
                }
                return null;
              }}
              renderFooter={(props) => {
                return (
                  <FerryChildSelect
                    childCount={props.tempValues.child}
                    tempValues={props.tempValues}
                    setTempValues={props.setTempValues}
                  />
                );
              }}
              className="[&_.selection-button]:font-semibold [&_.selection-footer]:border-none"
              selectionItemsClassName="max-h-[300px] overflow-y-auto divide-y divide-primary-weak"
              scrollToForm={scrollToForm}
            />
          </div>

          {/* Search Button */}
          <div className="p-3 md:p-0">
            <button
              type="button"
              onClick={validateAndSearch}
              className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 text-lg font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] md:w-max"
            >
              {/* Yazı: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <span
                className={clsx("whitespace-nowrap", searchButtonTextClassName)}
              >
                {tForm("ferry.searchButton")}
              </span>

              {/* Turna icon: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <FontAwesomeIcon
                className={clsx(
                  "max-w-[24px] text-[24px]",
                  searchButtonTurnaIconClassName,
                )}
                icon={byPrefixAndName.fak["button-turna-icon"]}
              />

              {/* Büyüteç: Sadece Tablet'te göster */}
              <FontAwesomeIcon
                className={clsx(
                  "max-w-[32px] text-[32px]",
                  searchButtonMagnifierIconClassName,
                )}
                icon={byPrefixAndName.fak["search-button-icon"]}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form;
